import React from 'react'
import { Box, CircularProgress, Stack } from '@mui/material'
import { Add as AddIcon } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import {
  NotionButton,
  PageScaffold,
  SubsectionHeader,
} from '@/apps/latest/components/portal/notion'
import { useToast } from '@/shared/contexts/ToastContext'
import { NSIPS_ERRORS } from '@/apps/latest/constants/nsips'
import {
  useNSIPSFormValidation,
  useNSIPSForm,
  useExportNSIPSMutation,
  PatientInfoSection,
  PrescriptionSection,
} from '@/features/internal'

export default function NSIPSExportPage() {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const exportMutation = useExportNSIPSMutation()
  const { validateForm } = useNSIPSFormValidation()
  const {
    formData,
    errors,
    setErrors,
    handlePatientFieldChange,
    handlePrescriptionFieldChange,
    handleDrugFieldChange,
    handleDrugSelect,
    addPrescription,
    removePrescription,
    addDrug,
    removeDrug,
  } = useNSIPSForm()

  const handleValidateForm = (): boolean => {
    const newErrors = validateForm(formData)
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleExport = async () => {
    if (!handleValidateForm()) {
      showToast(NSIPS_ERRORS.VALIDATION_ERROR, 'error')
      return
    }
    await exportMutation.mutateAsync(formData)
  }

  const loading = exportMutation.isPending

  return (
    <PageScaffold title="NSIPS データエクスポート">
      <Stack gap={4}>
        <Box>
          <SubsectionHeader heading="患者情報" />
          <PatientInfoSection
            formData={formData}
            errors={errors}
            onChange={handlePatientFieldChange}
          />
        </Box>

        <Box>
          <SubsectionHeader heading="処方内容" />
          <Stack gap={2}>
            {formData.prescriptions.map((prescription, rpIndex) => (
              <PrescriptionSection
                key={rpIndex}
                prescription={prescription}
                rpIndex={rpIndex}
                errors={errors}
                canDelete={formData.prescriptions.length > 1}
                onUsageChange={handlePrescriptionFieldChange(rpIndex, 'usage')}
                onDaysChange={handlePrescriptionFieldChange(rpIndex, 'days')}
                onDrugFieldChange={(drugIndex, field) =>
                  handleDrugFieldChange(rpIndex, drugIndex, field)
                }
                onDrugSelect={(drugIndex) =>
                  handleDrugSelect(rpIndex, drugIndex)
                }
                onAddDrug={() => addDrug(rpIndex)}
                onRemoveDrug={(drugIndex) => removeDrug(rpIndex, drugIndex)}
                onRemovePrescription={() => removePrescription(rpIndex)}
              />
            ))}

            <NotionButton
              variant="secondary"
              startIcon={<AddIcon />}
              onClick={addPrescription}
              sx={{ alignSelf: 'flex-start' }}
            >
              RP を追加
            </NotionButton>
          </Stack>
        </Box>
      </Stack>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 4,
        }}
      >
        <NotionButton
          variant="secondary"
          onClick={() => navigate('/portal')}
          disabled={loading}
        >
          キャンセル
        </NotionButton>
        <NotionButton
          variant="primary"
          onClick={handleExport}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={16} sx={{ color: 'inherit' }} />
          ) : (
            'エクスポート実行'
          )}
        </NotionButton>
      </Box>
    </PageScaffold>
  )
}
